import React, { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import Sheet from 'components/sheet/Sheet';
import DataTable from 'components/dataTable/DataTable';
import { Badge, IBadgeProps } from 'components/badge/Badge';
import { ButtonNew } from 'components/buttonNew';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowDown, faCircleArrowUp, faDownload, faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import Input from 'components/form/input/Input';
import { showError, showSuccess } from 'redux/actions/ui';
import { useDispatch } from 'react-redux';
import { downloadCsv } from 'helpers/Helpers';
import { IAudienceCustomer, PostAudienceCustomers } from 'api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns';
import { StatusCode } from 'api/protocols';
import Callout from 'components/callout/Callout';
import useDebounce from 'hooks/events/useDebounce';
import SelectNew from 'components/select/SelectNew';
import _ from 'lodash';
import SelectCustomers from 'components/selectCustomers/SelectCustomers';
import { ICustomer } from 'redux/reducers/models/customer';
import Checkbox from 'components/form/checkbox/Checkbox';
import Spin from 'components/spin/spin';

type TImportOptions = 'append' | 'replace';

interface IProps {
  customers: IAudienceCustomer[];
  onImport?: (customers: IAudienceCustomer[], importType: TImportOptions) => void; // After save
  onExport?: () => void;
  onPageChange: (dir: 'next' | 'prev', limit: number, offset: number) => void;
  onCustomerSearch: (search: string) => void;
  onIncludeToggle?: (customer: IAudienceCustomer) => void;
  limit: number,
  offset: number,
  total: number,
  includedCount: number;
  selectable?: boolean,
  loading?: boolean,
}
export default function AudienceCustomersTable({
  customers,
  onExport,
  onImport,
  onPageChange,
  onCustomerSearch,
  onIncludeToggle,
  limit,
  offset,
  total,
  selectable = true,
  loading = false,
  includedCount,
}: IProps) {
  const dispatch = useDispatch();

  const [openCustomerImport, setOpenCustomerImport] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(
    search,
    200,
  );
  const [selectedFile, setSelectedFile] = useState<File>(null);
  const fileUploadRef = useRef(null);
  const [importType, setImportType] = useState<TImportOptions>('replace');
  const [openAddCustomer, setOpenAddCustomer] = useState(false);

  function getImportTemplate() {
    const csv = "first_name,last_name,email,phone,username,preference_marketing_email\n"
      + "First,Last,email@example.com,,,true\n"
      + "First,Last,email@example.com,,,false";
    downloadCsv(csv, 'customer_import.csv');
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      setSelectedFile(file[0]);
    }
  }

  async function importCustomerCsv() {
    const formData = new FormData();
    formData.append('csv', selectedFile);

    const resp = await PostAudienceCustomers(formData, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    if (onImport != null) {
      onImport(resp.data, importType);
    }
    setOpenCustomerImport(false);
    dispatch(showSuccess(t('customers imported')));
  }

  function handleCustomerSearch(search: string) {
    setSearch(search);
  }

  useEffect(() => {
    onCustomerSearch(search);
  }, [debouncedSearch]);

  function badgeStatus(customer: IAudienceCustomer): IBadgeProps['type'] {
    if (customer.status === 'unsubscribed') {
      return 'error';
    }

    if (customer.excluded || customer.status === 'excluded') {
      return 'warning';
    }

    if (customer.status === 'active') {
      return 'success';
    }


    return 'gray';
  }

  function handleAddCustomer(addedCustomers: ICustomer[]) {
    const added: IAudienceCustomer[] = addedCustomers.map(c => ({
      id: c.id,
      first_name: c.first_name,
      last_name: c.last_name,
      email: c.email,
      created_at: '',
      status: 'active',
      preference_marketing_email: false,
      excluded: false,
    }));
    onImport(added, 'append');
    setOpenAddCustomer(false);
    setSearch('');
  }

  return <>
    <div className="text-lg font-semibold flex gap-2 items-center mb-2">
      {t('Smart List Customers')}
      <Badge>{loading ? '0' : includedCount} of {loading ? '0' : total} {t('Selected')}</Badge>
      <div className="grow"></div>
      {onExport != null &&
        <ButtonNew onClick={() => onExport()} className="ml-0">
          <FontAwesomeIcon icon={faCircleArrowUp} className="mr-1" />
          {t('Export')}
        </ButtonNew>
      }
      {onImport != null &&
        <ButtonNew onClick={() => setOpenCustomerImport(true)} className="ml-0">
          <FontAwesomeIcon icon={faCircleArrowDown} className="mr-1" />
          {t('Import')}
        </ButtonNew>
      }
      <Input
        placeholder={t("Search name or email")}
        value={search}
        onChange={e => handleCustomerSearch(e.target.value)}
        prefix={<FontAwesomeIcon icon={faSearch} />}
        labelHidden={true}
      />
    </div>

    <DataTable
      columns={selectable ?
        [
          { label: t('Include') },
          { label: t('Name') },
          { label: t('Email address') },
          { label: t('Status') },
        ] :
        [
          { label: t('Name') },
          { label: t('Email address') },
          { label: t('Status') },
        ]}
      footer={{
        tableLimit: limit,
        tableOffset: offset,
        handleTableOffset: dir => onPageChange(dir, limit, offset),
        disableNextOffset: (offset + limit) > total,
      }}
    >
      {loading && <tr>
        <td colSpan={5} className="text-center">
          <div className="h-8 flex justify-center gap-4 items-center">
            {t('Loading...')}
            <Spin />
          </div>
        </td>
      </tr>}
      {!loading && customers.length === 0 && <tr>
        <td colSpan={5} className="text-center">
          {t('No customers found with current filters.')}
          {onImport && <div className="flex justify-center items-center w-full mt-2">
            <ButtonNew type="primary" size="small" onClick={() => setOpenAddCustomer(true)}>
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {t('Add Customer')}
            </ButtonNew>
          </div>}
        </td>
      </tr>}
      {!loading && customers.map((customer, i) =>
        <tr key={customer.id.toString() + '-' + customer.status + '-' + i} >
          {selectable && <td onClick={() => selectable && onIncludeToggle && onIncludeToggle(customer)} className="cursor-pointer">
            {customer.status !== 'unsubscribed' && <Checkbox
              size="small"
              readOnly={true}
              disabled={!selectable}
              checked={!customer.excluded}
            />}
          </td>}
          <td>{customer.first_name} {customer.last_name}</td>
          <td>{customer.email}</td>
          <td><Badge type={badgeStatus(customer)}>{_.startCase(customer.excluded ? t('Excluded') : customer.status ?? 'undef')}</Badge></td>
        </tr>,
      )}
    </DataTable>

    <Sheet
      open={openCustomerImport}
      okText={t('Import')}
      onOk={importCustomerCsv}
      onSecondary={getImportTemplate}
      onCancel={() => setOpenCustomerImport(false)}
      secondaryText={<div><FontAwesomeIcon icon={faDownload} /> {t('CSV Template')}</div>}
      title={t('Import Customer List')}
      okDisabled={selectedFile == null}
    >
      <div className="text-md">
        <div>{t('Import a fixed list of customer to receive email campaigns.')}</div>
        <div>{t('New customers will be created if they do not exist.')}</div>
      </div>

      <div className="my-4">
        <SelectNew
          id="audience_customer_import_type"
          label={t("Choose how to import these customers.")}
          options={[
            { label: t("Replace manually imported customers."), id: "replace" },
            { label: t("Add new customers to existing list"), id: "append" },
          ]}
          onChange={(value: 'replace' | 'append') => setImportType(value)}
          value={importType}
        >
        </SelectNew>
      </div>
      <Callout
        type="info"
        title={!selectedFile ? t("No file selected") : t("Selected File")}
        content={!selectedFile ? t("Select a file to continue") : selectedFile.name}
      />
      <div className="file-import-container my-4">
        <input
          ref={fileUploadRef}
          className="file-import-input"
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
        />
        <FontAwesomeIcon className="file-import-icon" icon={["far", "upload"]} />
      </div>
    </Sheet>
    <SelectCustomers
      open={openAddCustomer}
      onOk={handleAddCustomer}
      onClose={() => setOpenAddCustomer(false)}
    />
  </>;
}
